import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { caseInsensitiveSort } from 'utils'
import { signOut } from 'store/auth'

export const initialState = {
  error: null,
  inProgress: false,
  moderators: [],
  moderator: null,
  currentPage: 0,
  pageSize: 10
}

const moderatorSlice = createSlice({
  name: 'moderator',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setModerators: (state, action) => {
      state.moderators = action.payload
    },
    setModerator: (state, action) => {
      state.moderator = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.moderators = []
      state.moderator = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const addModerator = (conferenceId, payload) => async (dispatch) => {
  try {
    dispatch(moderatorSlice.actions.setProgress(true))
    const doc = await FirestoreDatabase.collection(Collection.MODERATORS).add({
      ...payload,
      conferenceId: conferenceId
    })
    return doc.id
  } catch (err) {
    dispatch(moderatorSlice.actions.setError(err))
  } finally {
    dispatch(moderatorSlice.actions.setProgress(false))
  }
}

export const checkModeratorIsUnique = (fullName) => async (dispatch, getState) => {
  try {
    dispatch(moderatorSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.MODERATORS)
      .where('fullName', '==', fullName)
      .where('conferenceId', '==', user?.conferenceId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(moderatorSlice.actions.setError(err))
  } finally {
    dispatch(moderatorSlice.actions.setProgress(false))
  }
}

export const checkModeratorExistsInEventsOrSessions =
  (moderatorId) => async (dispatch, getState) => {
    try {
      dispatch(moderatorSlice.actions.setProgress(true))
      const user = getState().auth.user
      const modExistInEvent = await FirestoreDatabase.collection(Collection.EVENTS)
        .where('moderators', 'array-contains', moderatorId)
        .where('conferenceId', '==', user?.conferenceId)
        .get()

      const modExistInSession = await FirestoreDatabase.collection(Collection.SESSIONS)
        .where('moderators', 'array-contains', moderatorId)
        .where('conferenceId', '==', user?.conferenceId)
        .get()

      return !modExistInEvent.empty || !modExistInSession.empty
    } catch (err) {
      dispatch(moderatorSlice.actions.setError(err))
    } finally {
      dispatch(moderatorSlice.actions.setProgress(false))
    }
  }

export const fetchModerators = () => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    dispatch(moderatorSlice.actions.setProgress(true))
    FirestoreDatabase.collection(Collection.MODERATORS)
      .where('conferenceId', '==', user?.conferenceId)
      .onSnapshot((documents) => {
        const moderators = []
        documents.forEach((doc) => {
          moderators.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(moderatorSlice.actions.setModerators(caseInsensitiveSort(moderators, 'fullName')))
      })
  } catch (err) {
    dispatch(moderatorSlice.actions.setError(err))
  } finally {
    dispatch(moderatorSlice.actions.setProgress(false))
  }
}

export const fetchModeratorById = (moderatorId) => async (dispatch) => {
  try {
    dispatch(moderatorSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.MODERATORS)
      .doc(moderatorId)
      .get()
    const e = snapshot.data()
    dispatch(
      moderatorSlice.actions.setModerator({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(moderatorSlice.actions.setError(err))
  } finally {
    dispatch(moderatorSlice.actions.setProgress(false))
  }
}

export const updateModeratorById = async (moderatorId, payload) => {
  try {
    await FirestoreDatabase.collection(Collection.MODERATORS)
      .doc(moderatorId)
      .update({
        ...payload
      })
    return moderatorId
  } catch (err) {
    console.error(err)
  }
}

export const deleteModeratorById = (moderatorId) => async (dispatch) => {
  try {
    dispatch(moderatorSlice.actions.setProgress(true))
    await FirestoreDatabase.collection(Collection.MODERATORS).doc(moderatorId).delete()
    return true
  } catch (err) {
    dispatch(moderatorSlice.actions.setError(err))
  } finally {
    dispatch(moderatorSlice.actions.setProgress(false))
  }
}

export const resetModerator = () => (dispatch) => {
  dispatch(moderatorSlice.actions.setModerator(null))
}

export const bulkInsertModerators = (moderators) => async (dispatch) => {
  try {
    dispatch(moderatorSlice.actions.setProgress(true))
    const collectionRef = FirestoreDatabase.collection(Collection.MODERATORS)
    const batch = FirestoreDatabase.batch()
    moderators.forEach((docData) => {
      const docRef = collectionRef.doc()
      batch.set(docRef, docData)
    })
    await batch.commit()
  } catch (err) {
    dispatch(moderatorSlice.actions.setError(err))
  } finally {
    dispatch(moderatorSlice.actions.setProgress(false))
  }
}

export default moderatorSlice
